// FR-A3: complete a password reset from the emailed link (`{public_base_url}/reset-password?
// token=gpr_…`, handlers::send_password_reset). Success revokes every session (server-side),
// so this always routes back to sign-in rather than trying to establish one here.
import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import { AuthShell } from "../components/AuthShell";
import { Input } from "@grackle/ui";
import { ButtonLink, FormNotice, LoadingButton } from "../components/form";

const PASSWORD_MIN = 10;
const PASSWORD_MAX = 128;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [confirmError, setConfirmError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthShell title="That link didn't work" subtitle="It's missing a reset token — request a new one.">
        <ButtonLink to="/forgot-password" block>
          Request a new link
        </ButtonLink>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password reset" subtitle="Sign in with your new password — every other session was signed out.">
        <ButtonLink to="/signin" block>
          Sign in
        </ButtonLink>
      </AuthShell>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(undefined);
    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
      setPasswordError(`At least ${PASSWORD_MIN} characters`);
      return;
    }
    setPasswordError(undefined);
    if (password !== confirmPassword) {
      setConfirmError("Passwords don't match");
      return;
    }
    setConfirmError(undefined);
    setSubmitting(true);
    try {
      await api("POST", "/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiFail) {
        setFormError(err.code === "validation" ? "This reset link is invalid or has expired." : err.message);
      } else {
        setFormError("Couldn't reach Grackle. Check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title="Choose a new password" subtitle="This replaces your current password and signs out every other device.">
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          help={`At least ${PASSWORD_MIN} characters`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={passwordError}
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••••••"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={confirmError}
        />
        {formError ? <FormNotice tone="error">{formError}</FormNotice> : null}
        <LoadingButton type="submit" block loading={submitting}>
          {submitting ? "Resetting…" : "Reset password"}
        </LoadingButton>
      </form>
    </AuthShell>
  );
}
